import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Measures the height an element may take so that the page itself never scrolls.
 *
 * CLAUDE.md "A screen header never scrolls" is the spec, and each rule in it is here because
 * the same mistake was made screen by screen:
 *
 *  - `height: 100%` resolves against nothing (the Classic shell's `<main>` is unbounded), so
 *    the height is MEASURED: window height (or, inside the workspace shell, the bottom edge of
 *    its main area), minus the element's top edge, minus the bottom padding of the enclosing
 *    `<main>`. No typed offset describes another element's size.
 *  - A CALLBACK ref, not `useRef`: the element often mounts after a loading gate, and an
 *    effect keyed on a ref runs once against `null` and never applies a height.
 *  - Re-measured on window resize and whenever the element's parent or `<main>` resizes
 *    (a header that grows pushes the element down).
 *
 * Returns `[ref, height]`; `height` is undefined until the first measurement.
 */
export function useFillHeight<T extends HTMLElement>(): [
  (node: T | null) => void,
  number | undefined,
] {
  const [node, setNode] = useState<T | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const frame = useRef(0);

  const ref = useCallback((el: T | null) => setNode(el), []);

  useEffect(() => {
    if (!node) return;

    const measure = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        const top = node.getBoundingClientRect().top;
        const main = node.closest('main, [data-ws-main]');
        const bottomPad = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
        // The workspace shell's main area has a definite height of its own (a grid row, which
        // gives way to a footer row when there is one), so measure to ITS bottom edge. Any
        // other `main` is unbounded and grows with its content, so the window is the limit.
        const limit = main?.hasAttribute('data-ws-main')
          ? main.getBoundingClientRect().bottom
          : window.innerHeight;
        const next = Math.max(160, Math.floor(limit - top - bottomPad));
        setHeight((prev) => (prev === next ? prev : next));
      });
    };

    measure();
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    if (node.parentElement) observer.observe(node.parentElement);
    const main = node.closest('main, [data-ws-main]');
    if (main) observer.observe(main);
    // Content above the element (title, tabs, filters) changing height moves its top edge.
    let sibling = node.previousElementSibling;
    while (sibling) {
      observer.observe(sibling);
      sibling = sibling.previousElementSibling;
    }

    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [node]);

  return [ref, height];
}
